import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

const PLANS = [
  {
    tier: "free",
    name: "Free",
    price: 0,
    tagline: "Start free. Ship a media kit today.",
    features: ["Live Media Kit", "Rate Calculator", "1 contract scan / month", "5 Playbooks + 3 outbound pitches / mo"],
    cta: "Current plan",
    lookup: null,
  },
  {
    tier: "starter",
    name: "Starter",
    price: 9,
    tagline: "Grow your audience with insider playbooks.",
    features: [
      "Everything in Free",
      "Full Playbook Library (all 15)",
      "Weekly Trend Radar",
      "20 outbound pitches / mo",
      "3 contract scans / mo",
    ],
    cta: "Upgrade to Starter",
    lookup: "dap_starter_monthly",
  },
  {
    tier: "pro",
    name: "Pro",
    price: 29,
    tagline: "Everything to close deals faster.",
    features: [
      "Everything in Starter",
      "Deal Copilot (streaming AI replies)",
      "Unlimited outbound + scans",
      "Deal pipeline Kanban",
    ],
    cta: "Upgrade to Pro",
    lookup: "dap_pro_monthly",
    accent: true,
  },
  {
    tier: "business",
    name: "Business",
    price: 59,
    tagline: "Scale like a small agency.",
    features: [
      "Everything in Pro",
      "Tax + income tracker (coming soon)",
      "Multi-brand dashboard",
      "Priority AI models",
    ],
    cta: "Go Business",
    lookup: "dap_business_monthly",
  },
];

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);

  const upgrade = async (lookup_key) => {
    track("dap_upgrade_clicked", { lookup_key, source: "billing_page" });
    if (!user) {
      nav("/signup");
      return;
    }
    setBusy(lookup_key);
    try {
      const r = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// pricing</p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        Pick your plan.
      </h1>
      <p className="text-neutral-400 mb-12 max-w-2xl">
        Cancel anytime. Test cards work — this is sandbox mode.
      </p>

      <div className="grid md:grid-cols-3 gap-4">
        {PLANS.map((p) => {
          const isCurrent = user?.plan === p.tier;
          const isAccent = p.accent;
          return (
            <div
              key={p.tier}
              className={`p-8 rounded-2xl relative ${
                isAccent
                  ? "bg-[#CCFF00] text-black"
                  : "bg-[#141414] border border-white/5"
              }`}
              data-testid={`plan-card-${p.tier}`}
            >
              {isAccent && (
                <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-black text-[#CCFF00] px-2 py-1 rounded">
                  Popular
                </div>
              )}
              <p
                className={`font-mono text-xs uppercase tracking-widest mb-2 ${
                  isAccent ? "text-black/70" : "text-neutral-500"
                }`}
              >
                {p.name}
              </p>
              <p className="font-display font-black text-5xl mb-1 tracking-tighter">
                ${p.price}
                {p.price > 0 && <span className="text-lg">/mo</span>}
              </p>
              <p className={`text-sm mb-6 ${isAccent ? "text-black/70" : "text-neutral-400"}`}>
                {p.tagline}
              </p>
              <ul className="space-y-2 mb-8 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isAccent ? "text-black" : "text-[#CCFF00]"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.lookup ? (
                <Button
                  onClick={() => upgrade(p.lookup)}
                  disabled={busy === p.lookup || isCurrent}
                  className={`w-full h-12 rounded-full font-semibold ${
                    isAccent
                      ? "bg-black text-[#CCFF00] hover:bg-neutral-900"
                      : "bg-[#CCFF00] text-black hover:bg-[#b8e600]"
                  }`}
                  data-testid={`plan-cta-${p.tier}`}
                >
                  {busy === p.lookup ? "Redirecting…" : isCurrent ? "Current plan" : p.cta}
                  {!isCurrent && busy !== p.lookup && <Sparkles className="w-4 h-4 ml-1" />}
                </Button>
              ) : (
                <Button
                  disabled
                  className="w-full h-12 rounded-full bg-white/5 text-neutral-500 border border-white/10"
                >
                  {isCurrent ? "Current plan" : p.cta}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-xs font-mono text-neutral-500 text-center">
        Payments processed by Stripe (sandbox). Test with card 4242 4242 4242 4242, any future expiry, any CVC.
      </p>
    </div>
  );
}
